import React, { useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import clsx from 'clsx'
import moment from 'moment'
import { fetchData, toasterrormsg } from '../../../../reusable'
import Select from 'react-select';
import { KTIcon } from '../../../helpers'
import { Tr } from '../../../i18n/Tr'

type Props = {
  className: string
}

const CouponCharts: React.FC<Props> = ({ className }) => {
  const t = Tr()
  const categoryId = sessionStorage.getItem('categoryId');
  var vendorId = "";
  const vendorData = sessionStorage.getItem("vendor");
  if (vendorData) {
    var vendor = JSON.parse(vendorData);
    vendorId = vendor.vendorId;
  } else {
    console.log("No vendor data found");
  }

  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const [tab, setTab] = useState('Weekly') // Weekly, Monthly, Yearly, Custom
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'))
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'))
  const [yearList,setYearList] = useState([]) as any
  const [yearSelectedOption,setYearSelectedOption] = useState({ label: moment().year(), value: moment().year() }) as any
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
  const [selectedWeekStartDate, setSelectedWeekStartDate] = useState(moment().startOf('isoWeek').format('YYYY-MM-DD'));
  const [categoryReport,setCategoryReport] = useState([]) as any
  const [axisData,setAxisData] = useState([]) as any
  
  const getVendorId = (): string => {
    const raw = sessionStorage.getItem('vendor')
    const v = raw ? JSON.parse(raw) : null
    return v?.vendorId ? String(v.vendorId) : ''
  }

  // view vendor profile api call
  const viewProfileApiCall = async () => {
    const _vendorId = getVendorId()
    if (!_vendorId) return
    var param: any = {
      vendorId: _vendorId,
    }
    var res = await fetchData('public/viewProfile', param)

    if (res.status == '200') {
      var data = res.data[0]
      const vendorCreatedYear = moment(data.created).year()
      const currentYear = moment().year()
      const tempYearList = [];
      for (let i = currentYear; i >= vendorCreatedYear; i--) {
        tempYearList.push({ label: `${i}`, value: `${i}` }); // Push year to list
      }
      setYearList(tempYearList);
      
    } else {
      toasterrormsg(res.message)
    }
  }
  useEffect(() => {
    viewProfileApiCall()
  }, []);

  // Function to handle Next Month
  const handleNextMonth = () => {
    const nextMonth = moment(selectedMonth).add(1, 'month').format('YYYY-MM');
    setSelectedMonth(nextMonth);
  };

  // Function to handle Previous Month
  const handlePreviousMonth = () => {
    const prevMonth = moment(selectedMonth).subtract(1, 'month').format('YYYY-MM');
    setSelectedMonth(prevMonth);
  };

  // Function to handle Next Week
  const handleNextWeek = () => {
    const nextWeekStartDate = moment(selectedWeekStartDate).add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD')
    setSelectedWeekStartDate(nextWeekStartDate);
  };

  // Function to handle Previous Week
  const handlePreviousWeek = () => {
    const prevWeekStartDate = moment(selectedWeekStartDate).subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD')
    setSelectedWeekStartDate(prevWeekStartDate);
  };

  //  report list api call
  const reportListApiCall = async () => {
    var param: any = {
      vendorId:vendorId,
      categoryId:categoryId,
    }
    if(tab == 'Weekly'){
      param['type'] = 'WEEKLY'
      param['typeData'] = String(selectedWeekStartDate)
    }else if(tab == 'Monthly'){
      param['type'] = 'MONTHLY'
      param['typeData'] = String(selectedMonth)
    }else if(tab == 'Yearly'){
      param['type'] = 'YEARLY'
      param['typeData'] = String(yearSelectedOption.value)
    }else if(tab == 'Custom'){
      param['type'] = 'CUSTOM'
      param['fromDate'] = fromDate
      param['toDate'] = toDate
    }
    var response = await fetchData('public/viewCouponReport', param)
    
    if (response.status == '200') {
      var data = response.data[0]
      var category = []
      var axis = []
      axis = data.date.map((item: any) => item.totalCoupon)
      if(tab == 'Weekly'){
        category = data.date.map((item:any) => item.dayName +' '+item.date)
      }else if(tab == 'Monthly' || tab == 'Custom'){
        category = data.date.map((item:any) => item.date)
      }else if(tab == 'Yearly'){
        category = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      }
      setCategoryReport(category)
      setAxisData(axis)
    } else {
      toasterrormsg(response.message)
    }
  }

  useEffect(() => {
    reportListApiCall()
  }, [tab,yearSelectedOption, fromDate, toDate, selectedMonth, selectedWeekStartDate]);
  
  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, tab, categoryReport, axisData, fromDate, toDate, yearSelectedOption])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(chartRef.current, getChartOptions(height))
    if (chart) {
      chart.render()
    }

    return chart
  }

  const getChartOptions = (height: number): ApexOptions => {
    const labelColor = getCSSVariableValue('--bs-gray-500')
    const borderColor = getCSSVariableValue('--bs-gray-200')

    return {
      series: [
        {
          name: t('Coupon'),
          data: axisData, // Replace with actual data
        },
      ],
      chart: {
        fontFamily: 'inherit',
        type: 'bar',
        height: height,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '30%',
          borderRadius: 5,
        },
      },
      xaxis: {
        categories: categoryReport,
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
        },
      },
      tooltip: {
        style: {
          fontSize: '12px',
        },
        y: {
          formatter: function (val) {
            return val + ''
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ['#65C098'],
      grid: {
        borderColor: borderColor,
      },
    }
  }

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-4 mb-1 mt-5'>{t('Coupon Report')}</span>
        </h3>
        <div className='card-toolbar'>
          <div className='card card-custom'>
            <div className='card-header border-0 card-header-stretch'>
              <ul className='nav nav-stretch nav-line-tabs fw-semibold border-0 align-items-center flex-nowrap'>
                <li className='nav-item'>
                  <button className={clsx('btn btn-sm mx-1', { 'btn-primary': tab === 'Weekly' })}
                    onClick={() => setTab('Weekly')}
                  >
                    {t('Weekly')}
                  </button>
                </li>
                <li className='nav-item'>
                  <button className={clsx('btn btn-sm mx-1', { 'btn-primary': tab === 'Monthly' })}
                    onClick={() => setTab('Monthly')}
                  >
                    {t('Monthly')}
                  </button>
                </li>
                <li className='nav-item'>
                  <button className={clsx('btn btn-sm mx-1', { 'btn-primary': tab === 'Yearly' })}
                    onClick={() => setTab('Yearly')}
                  >
                    {t('Yearly')}
                  </button>
                </li>
                <li className='nav-item'>
                  <button className={clsx('btn btn-sm mx-1', { 'btn-primary': tab === 'Custom' })}
                    onClick={() => setTab('Custom')}
                  >
                    {t('Custom')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body'>
        {
          tab === 'Weekly' && (
            <div className="row">
              <div className="col-lg-12 d-flex justify-content-end align-items-center">
                <button className='btn btn-sm mx-1 border border-success btn-active-light-success' onClick={handlePreviousWeek}><KTIcon iconName='arrow-left' className='fs-3' /></button>
                <button className='btn btn-sm mx-1 border border-success btn-active-light-success' onClick={handleNextWeek}><KTIcon iconName='arrow-right' className='fs-3' /></button>
              </div>
            </div>
          )
        }
        {
          tab === 'Monthly' && (
            <div className="row">
              <div className="col-lg-12 d-flex justify-content-end align-items-center">
                <button className='btn btn-sm mx-1 border border-success btn-active-light-success' onClick={handlePreviousMonth}><KTIcon iconName='arrow-left' className='fs-3' /></button>
                <div className='form-label mt-1 fs-3 mx-3'>{moment(selectedMonth).format('MMMM')}</div>
                <button className='btn btn-sm mx-1 border border-success btn-active-light-success' onClick={handleNextMonth}><KTIcon iconName='arrow-right' className='fs-3' /></button>
              </div>
            </div>
          )
        }
        {
          tab === 'Yearly' && (
            <div className="row">
              <div className="col-lg-9 col-6"></div>
              <div className="col-lg-3 col-6">
                <Select 
                    name='year[]'
                    id='year'
                    placeholder={t('Select Year')}
                    isSearchable={true}
                    options={yearList}
                    value={yearSelectedOption}
                    onChange={(e:any) =>{setYearSelectedOption(e);}}
                />
              </div>
            </div>
          )
        }
        {tab === 'Custom' && (
          <div className="row">
            <div className="col-lg-4 my-2">
              <input type='date' className='form-control' max={moment().format('YYYY-MM-DD')} onKeyDown={(e) => e.preventDefault()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="col-lg-4"></div>
            <div className="col-lg-4 my-2">
              <input type='date' className='form-control' max={moment().format('YYYY-MM-DD')} onKeyDown={(e) => e.preventDefault()} value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
        )}
        <div className='tab-content pt-3'>
          <div className={clsx('tab-pane', { active: tab === 'Weekly' || tab === 'Monthly' || tab === 'Yearly' || tab === 'Custom' })}>
            <div ref={chartRef} id='kt_charts_widget_1_chart' style={{ height: '450px' }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export { CouponCharts }
